/**
 * CustomerPickerDialog — search & attach a customer to the active cart.
 *
 * – Debounced search on `name` and `phone`. Results are limited to the
 *   active store and `is_deleted = false`.
 * – Keyboard-first: ↑/↓ to navigate the result list, Enter to attach,
 *   Esc to close (handled by Radix).
 * – "Walk-in" entry point at the top to clear any attached customer.
 * – Compact "no GSTIN, no phone" line so the cashier can spot duplicates
 *   at a glance.
 * – Quick-create inline form (name + phone only) for CUSTOMER_CREATE roles.
 * – Quick Bill mode: suppliers are listed too, and the quick-create form asks
 *   for an address. Picking a supplier bills them as a customer — found again
 *   by GSTIN/phone, or created once from their details (billing/billToParty.ts).
 */

import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Building2, Search, User, UserPlus, X } from 'lucide-react';
import { database, txn } from '@/db';
import { Customer } from '@/db/models/Customers';
import { Supplier } from '@/db/models/Inventory';
import { customerForSupplier } from '@/billing/billToParty';
import { useAppStore } from '@/store/useAppStore';
import { GSTIN_REGEX } from '@bigxbook/gst-core';
import { useCartStore, type CustomerRef } from '@/store/useCartStore';
import { useObservable } from '@/hooks/useObservable';
import { usePermissions, CUSTOMER_CREATE } from '@/rbac';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

interface Props {
  onClose: () => void;
}

function toRef(c: Customer): CustomerRef {
  return {
    id: c.id,
    name: c.name,
    phone: c.phone,
    gstin: c.gstin,
    state: c.state,
  };
}

export function CustomerPickerDialog({ onClose }: Props) {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const attached = useCartStore((s) => s.customer);
  const setCustomer = useCartStore((s) => s.setCustomer);
  const quickBill = useCartStore((s) => s.quickBill);
  const { can } = usePermissions();

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Quick-create state.
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createPhone, setCreatePhone] = useState('');
  const [createGstin, setCreateGstin] = useState('');
  // Asked only in Quick Bill mode, where the bill goes to a business that
  // needs its address on it.
  const [createAddress, setCreateAddress] = useState('');
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  useErrorPopup(createError, () => setCreateError(null));

  // Debounce typing.
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), 120);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reactive search.
  const customers = useObservable<Customer[]>(
    () => {
      if (!activeStoreId) return null;
      const conds: any[] = [
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
      ];
      if (searchQuery.length > 0) {
        const q = `%${searchQuery}%`;
        conds.push(
          Q.or(
            Q.where('name', Q.like(q)),
            Q.where('phone', Q.like(q)),
          ),
        );
      }
      return database
        .get<Customer>('customers')
        .query(...conds, Q.take(50))
        .observeWithColumns(['name', 'phone']);
    },
    [activeStoreId, searchQuery],
    [],
  );

  // Quick Bill: suppliers can be billed too.
  const suppliers = useObservable<Supplier[]>(
    () => {
      if (!quickBill) return null;
      const conds: any[] = [Q.where('is_deleted', false)];
      if (searchQuery.length > 0) {
        const q = `%${searchQuery}%`;
        conds.push(Q.or(Q.where('name', Q.like(q)), Q.where('phone', Q.like(q)), Q.where('gstin', Q.like(q))));
      }
      return database.get<Supplier>('suppliers').query(...conds, Q.take(20)).observeWithColumns(['name', 'phone', 'gstin']);
    },
    [quickBill, searchQuery],
    [],
  );
  const [supplierBusy, setSupplierBusy] = useState<string | null>(null);
  const [supplierError, setSupplierError] = useState<string | null>(null);
  useErrorPopup(supplierError, () => setSupplierError(null));
  async function billSupplier(sup: Supplier) {
    if (!activeStoreId) return;
    setSupplierBusy(sup.id);
    setSupplierError(null);
    try {
      attach(await customerForSupplier(activeStoreId, sup));
    } catch (err) {
      setSupplierError((err as Error).message || 'Could not use this supplier.');
      setSupplierBusy(null);
    }
  }

  // Stable key list to clamp focusIdx when result count changes.
  const ids = useMemo(() => customers.map((c) => c.id), [customers]);
  useEffect(() => {
    setFocusIdx((idx) => Math.max(0, Math.min(ids.length - 1, idx)));
  }, [ids.length]);

  const attach = (c: Customer | null) => {
    setCustomer(c ? toRef(c) : null);
    onClose();
  };

  // Open the quick-create form pre-filled from the current search — a numeric
  // query seeds the phone, anything else seeds the name — so a "not found"
  // search flows straight into "add & confirm".
  const startCreateFromSearch = () => {
    const term = searchInput.trim();
    if (!term) return;
    const numeric = /^\d+$/.test(term);
    if (numeric) {
      // A bare phone has no name yet — a name is required, so drop into the
      // quick-create form with the phone pre-filled and the name focused.
      setShowCreate(true);
      setCreateName('');
      setCreatePhone(term);
      setCreateError(null);
    } else {
      // We already have a name → create & attach immediately, no extra step.
      void createAndAttach(term, '');
    }
  };

  async function createAndAttach(name: string, phone: string): Promise<boolean> {
    const trimName = name.trim();
    const trimPhone = phone.trim();
    if (!trimName) { setCreateError('Name is required.'); return false; }
    if (trimPhone && !/^\d{10}$/.test(trimPhone)) { setCreateError('Phone must be 10 digits.'); return false; }
    const trimGstin = createGstin.trim().toUpperCase();
    if (trimGstin && !GSTIN_REGEX.test(trimGstin)) {
      setCreateError('That GSTIN is not a valid 15-character number. Leave it blank for a walk-in.');
      return false;
    }
    if (!activeStoreId) { setCreateError('No active store.'); return false; }
    setCreateError(null);
    setCreateSaving(true);
    try {
      let newCustomer: Customer;
      await txn(async (db) => {
        newCustomer = await db.get<Customer>('customers').create((record) => {
          record.storeId = activeStoreId;
          record.name = trimName;
          record.phone = trimPhone || null;
          record.email = null;
          record.gstin = trimGstin || null;
          record.pan = null;
          record.address = createAddress.trim() || null;
          record.city = null;
          record.state = null;
          record.pincode = null;
          record.groupId = null;
          record.creditLimit = 0;
          record.balance = 0;
          record.loyaltyTierId = null;
          record.loyaltyPoints = 0;
          record.totalSpend = 0;
          record.visitCount = 0;
          record.lastVisitAt = null;
          record.birthday = null;
          record.anniversary = null;
          record.tags = null;
          record.version = 1;
          record.isSynced = false;
          record.isDeleted = false;
          record.lastSyncBalance = null;
          record.lastSyncTotalSpend = null;
          record.lastSyncLoyaltyPoints = null;
          record.lastSyncVisitCount = null;
        });
      });
      // @ts-expect-error assigned in write callback
      attach(newCustomer);
      notifySuccess(`Customer “${trimName}” added to this bill.`);
      return true;
    } catch (err) {
      setCreateError((err as Error).message ?? 'Failed to create customer.');
      setCreateSaving(false);
      return false;
    }
  }

  async function handleQuickCreate(e: FormEvent) {
    e.preventDefault();
    await createAndAttach(createName, createPhone);
  }

  const onKeyDown: React.KeyboardEventHandler = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIdx((i) => Math.min(customers.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const c = customers[focusIdx];
      if (c) attach(c);
    }
  };

  // Scroll focused row into view on focus changes.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${focusIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [focusIdx]);

  return (
    <div className="flex flex-col gap-3 px-5 py-4" onKeyDown={onKeyDown}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary" />
        <Input
          ref={inputRef}
          autoFocus
          placeholder="Search by name or phone…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Walk-in / clear */}
      <button
        type="button"
        onClick={() => attach(null)}
        className={cn(
          'flex items-center justify-between rounded-md border border-stroke-hairline/15 bg-bg-canvas px-3 py-2',
          'text-body text-fg-secondary hover:border-accent/40 hover:text-fg-primary transition-colors',
          !attached && 'border-accent/50 bg-accent/5 text-fg-primary',
        )}
      >
        <span className="inline-flex items-center gap-2">
          <User size={14} /> Walk-in customer
        </span>
        {attached && (
          <span className="inline-flex items-center gap-1 text-label uppercase text-fg-tertiary">
            Detach <X size={12} />
          </span>
        )}
      </button>

      <ul
        ref={listRef}
        className="max-h-[360px] overflow-auto rounded-md border border-stroke-hairline/10 bg-bg-canvas"
      >
        {customers.length === 0 ? (
          <li className="px-3 py-6 text-center">
            {searchQuery ? (
              can(CUSTOMER_CREATE) ? (
                <div className="flex flex-col items-center gap-3">
                  <p className="text-body text-fg-tertiary">
                    No customer matches “{searchInput.trim()}”.
                  </p>
                  <Button size="sm" onClick={startCreateFromSearch}>
                    <UserPlus size={14} className="mr-1.5" />
                    Add “{searchInput.trim()}” as new customer
                  </Button>
                </div>
              ) : (
                <span className="text-body text-fg-tertiary">No matches.</span>
              )
            ) : (
              <span className="text-body text-fg-tertiary">Start typing to search customers.</span>
            )}
          </li>
        ) : (
          customers.map((c, idx) => {
            const focused = idx === focusIdx;
            const isAttached = attached?.id === c.id;
            return (
              <li key={c.id} data-idx={idx}>
                <button
                  type="button"
                  onClick={() => attach(c)}
                  onMouseEnter={() => setFocusIdx(idx)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left',
                    'transition-colors duration-120 ease-apple',
                    focused ? 'bg-accent/10' : 'hover:bg-bg-subtle',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-body text-fg-primary">
                        {c.name?.trim() || '—'}
                      </span>
                      {isAttached && (
                        <span className="rounded-full bg-accent/15 px-2 py-0.5 text-label uppercase text-accent-ink">
                          Attached
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-label uppercase text-fg-tertiary tabular-nums">
                      {c.phone || 'no phone'}
                      {c.gstin && <span className="ml-2">· {c.gstin}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 text-label uppercase text-fg-tertiary tabular-nums">
                    {c.balance > 0
                      ? `₹${c.balance.toFixed(0)} due`
                      : c.balance < 0
                        ? `₹${Math.abs(c.balance).toFixed(0)} adv`
                        : ''}
                  </div>
                </button>
              </li>
            );
          })
        )}
      </ul>

      {quickBill && suppliers.length > 0 && (
        <div>
          <p className="mb-1.5 text-label uppercase tracking-wide text-fg-tertiary">Suppliers</p>
          <ul className="max-h-[180px] overflow-auto rounded-md border border-stroke-hairline/10 bg-bg-canvas">
            {suppliers.map((sup) => (
              <li key={sup.id}>
                <button
                  type="button"
                  disabled={supplierBusy !== null}
                  onClick={() => { void billSupplier(sup); }}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-120 ease-apple hover:bg-bg-subtle disabled:opacity-60"
                >
                  <Building2 size={14} className="shrink-0 text-fg-tertiary" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-body text-fg-primary">{sup.name?.trim() || '—'}</div>
                    <div className="mt-0.5 truncate text-label uppercase text-fg-tertiary tabular-nums">
                      {sup.phone || 'no phone'}
                      {sup.gstin && <span className="ml-2">· {sup.gstin}</span>}
                    </div>
                  </div>
                  <span className="shrink-0 text-label text-accent-ink">
                    {supplierBusy === sup.id ? 'Adding…' : 'Bill to'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Quick-create — only visible to roles with CUSTOMER_CREATE permission */}
      {can(CUSTOMER_CREATE) && (
        <div className="border-t border-stroke-hairline/10 pt-3">
          {!showCreate ? (
            <button
              type="button"
              onClick={() => { setShowCreate(true); setCreateName(searchInput.trim()); setCreatePhone(''); setCreateGstin(''); setCreateAddress(''); setCreateError(null); }}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-stroke-hairline/15 bg-bg-canvas px-3 py-2 text-body text-fg-secondary hover:border-accent/40 hover:text-fg-primary transition-colors"
            >
              <UserPlus size={14} /> New customer
            </button>
          ) : (
            <form onSubmit={(e) => { void handleQuickCreate(e); }} className="space-y-2">
              <p className="text-label uppercase text-fg-tertiary tracking-wide">Quick create</p>
              <Input
                autoFocus
                placeholder="Name *"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                disabled={createSaving}
              />
              <Input
                placeholder="Phone (10 digits)"
                value={createPhone}
                onChange={(e) => setCreatePhone(e.target.value)}
                maxLength={10}
                inputMode="numeric"
                disabled={createSaving}
              />
              {/* A B2B buyer's GSTIN has to be captured HERE. Rule 46(e) puts
                  it on the tax invoice, and the invoice is built from the
                  customer record at billing time — so adding it afterwards is
                  too late for the bill that was already printed. */}
              <Input
                placeholder="GSTIN (for a business buyer)"
                value={createGstin}
                onChange={(e) => setCreateGstin(e.target.value.toUpperCase())}
                maxLength={15}
                spellCheck={false}
                disabled={createSaving}
              />
              {quickBill && (
                <Input
                  placeholder="Address (printed on the bill)"
                  value={createAddress}
                  onChange={(e) => setCreateAddress(e.target.value)}
                  disabled={createSaving}
                />
              )}
              <div className="flex gap-2">
                <Button type="submit" disabled={createSaving} className="flex-1">
                  {createSaving ? 'Saving…' : 'Save & attach'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setShowCreate(false); setCreateError(null); }}
                  disabled={createSaving}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
